import { UAContext } from "@quentin-sommer/react-useragent";
import classnames from "classnames";
import flat from "flat";
import React from "react";
import { useTranslation } from "react-i18next";
import { Sheet } from "react-modal-sheet";
import TextField from "~/components/Fields/TextField";
import Modal from "~/components/Modal";
import toastWithStyle from "~/helpers/toastWithStyle";
import { patchResource } from "~/services/api";
import to from "~/utils/to";

function EditEmail({
  visible,
  onClose,
  cookie,
  user,
}: {
  visible: boolean;
  onClose: () => void;
  cookie: any;
  user: any;
}) {
  const [submitting, setSubmitting] = React.useState(false);
  const { uaResults } = React.useContext(UAContext);
  const { t, i18n } = useTranslation();
  const [data, setData] = React.useState({
    "email.address": user?.email.address || "",
  });

  React.useEffect(() => {
    setData({ "email.address": user?.email.address || "" });
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const [err] = await to(
      patchResource({
        resource: "users",
        resourceId: user._id,
        token: cookie.token,
        data: flat.unflatten(data, { safe: true }),
      }),
    );
    setSubmitting(false);

    if (err) {
      return toastWithStyle({
        message: err.response?.data?.message,
        options: { type: "error" },
        uaResults,
        i18n,
      });
    }

    toastWithStyle({
      message: t("modals.editEmail.successMessage"),
      options: { type: "success" },
      uaResults,
      i18n,
    });
    onClose();
  };

  const Content = React.useMemo(() => {
    return (
      <div
        className="no-scrollbar relative border-0 bg-base-100 overflow-y-auto"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 2rem)" }}
      >
        <form
          className="h-full flex flex-col"
          aria-disabled={submitting}
          onSubmit={onSubmit}
        >
          <div className="mb-5 flex flex-col justify-center items-center gap-4 px-4 pb-4 md:mt-6">
            <div className="font-semibold text-xl">
              {t("modals.editEmail.email")}
            </div>
            <div className="text-center text-neutral-500 text-sm font-normal font-['Figtree', 'IRANYekanXVF'] leading-normal">
              {t("modals.editEmail.editYourEmail")}
            </div>
          </div>

          <div className="mb-5 flex flex-col gap-2 px-4 pb-4">
            <TextField
              input={{
                type: "text",
                name: "email.address",
                value: data["email.address"],
                placeholder: t("modals.editEmail.emailPlaceholder"),
                autoComplete: "email",
                onChange: handleInputChange,
                disabled: submitting,
              }}
              errorMode="subtitle"
            />
          </div>

          <div className="flex w-full flex-col items-center justify-center gap-7 rounded-xl px-4 text-center">
            <button
              disabled={
                submitting ||
                !data["email.address"] ||
                data["email.address"] === user?.email.address
              }
              className={classnames(
                "btn btn-block text-green-600 bg-green-200 text-base font-semibold font-['Figtree', 'IRANYekanXVF'] leading-normal justify-center text-center rounded-full",
              )}
              type="submit"
            >
              {!submitting && <span>{t("modals.editEmail.saveChanges")}</span>}
              {submitting && (
                <>
                  <span className="font-bold">
                    {t("modals.deleteUserConfirmation.pleaseWait")}
                  </span>
                  <span className="loading loading-spinner loading-md" />
                </>
              )}
            </button>

            <button
              onClick={onClose}
              className="btn-block text-neutral-900 text-base font-semibold font-['Figtree', 'IRANYekanXVF'] leading-normal"
              type="button"
            >
              {t("modals.editEmail.closeButton")}
            </button>
          </div>
        </form>
      </div>
    );
  }, [data, onClose, submitting, t]);

  if (uaResults.mobile) {
    return (
      <Sheet
        isOpen={visible}
        onClose={onClose}
        detent="content-height"
        tweenConfig={{ ease: "easeIn", duration: 0.3 }}
        style={{ zIndex: 50 }}
      >
        <Sheet.Container className="!rounded-t-3xl !bg-base-100">
          <Sheet.Header />
          <Sheet.Scroller>
            <Sheet.Content>{Content}</Sheet.Content>
          </Sheet.Scroller>
        </Sheet.Container>
        <Sheet.Backdrop className="backdrop-blur-sm" onTap={onClose} />
      </Sheet>
    );
  }
  return (
    <Modal
      isOpen={visible}
      onClose={onClose}
      classNames={{ dialog: "max-w-md" }}
    >
      {Content}
    </Modal>
  );
}

export default EditEmail;
